/**
 * publishFanoutDepsFromStore.ts
 *
 * Adapter that wraps `VideoCreatorStore` into the narrow `IPublishFanoutDeps`
 * interface `PublishFanoutService` depends on — the same seam as
 * `clipProcessorDepsFromStore` for `AIClipProcessor`. All store/rootStore
 * traversals live here so the service stays store-free and testable.
 *
 * `mainClip` is the parent clip being published (the 16:9 "main" video record
 * whose `associatedVideos` are the other AR variants). Passing it explicitly
 * decouples from `videoCreator.currentVideo` which may point to a child variant.
 */

import VideoPublisher from '@src/services/VideoPublisher';
import { populateVideoVersionWithResources } from '@src/services/VideoResourcesProvider';
import {
  getGraphicFontSubstitute,
  makeBrandFontVariantLookup,
} from '@src/utility/graphicFontSubstitution';
import { hydrateOriginalMediaDuration } from '@src/utility/timeline';
import { formatResolutionShortLabel } from '@src/utility/videoResolution';

import { PublishRecordService } from './PublishRecordService';
import { PublishSourceBuilder } from './PublishSourceBuilder';

import type { IPublishFanoutDeps } from './IPublishFanoutDeps';
import type { IVideoCreatorStore } from '@src/stores/IVideoCreatorStore';
import type { Video } from '@src/types.ts/story';

export function publishFanoutDepsFromStore(
  videoCreator: IVideoCreatorStore,
  mainClip: Video,
): IPublishFanoutDeps {
  const rootStore = videoCreator.rootStore;
  const publishRecordService = new PublishRecordService(
    videoCreator.datoClientStore.publishRecordRepository,
  );

  // Indexed once: the fan-out calls the resolver for every translated overlay
  // in every cell, and the workspace's font list does not change mid-publish.
  const brandVariants = makeBrandFontVariantLookup(
    videoCreator.organization?.branding?.customFonts,
  );

  // One builder serves every cell — `buildSource` seeds a fresh headless editor
  // per call, so it holds no per-cell state. Its phrase/lookup/hydrate deps are
  // wired here so the service needn't know about them.
  const sourceBuilder = new PublishSourceBuilder(
    language =>
      rootStore.translationStore.getTranslation(language)?.phrases ?? null,
    language =>
      rootStore.translationStore.getCompositionPhrasesByNormalizedSource(
        language,
      ),
    source => hydrateOriginalMediaDuration(source, videoCreator),
    // A function rather than the store itself: the builder must not import one.
    probe => videoCreator.isOriginalVideoElement(probe),
    // PLAT-11466 — a published video must correct translated overlay fonts
    // exactly as the canvas does, or the render disagrees with the preview.
    (family, lang) => getGraphicFontSubstitute(family, lang, brandVariants),
  );

  return {
    get story() {
      return videoCreator.story ?? undefined;
    },

    get organization() {
      return videoCreator.organization;
    },

    saveLiveEditor: async translatedPublish => {
      if (translatedPublish) {
        videoCreator.addVideoAction({
          editor: videoCreator.rootStore.userIdentityStore.currentEditor,
          date: Date.now(),
          type: 'publish',
          resolution: formatResolutionShortLabel(translatedPublish.resolution),
          languages: translatedPublish.languages,
          groupId: translatedPublish.groupId,
        });
      }
      // Persist the active AR (asFinal=false, withRenderer=true, resetTimeline
      // =false, autoSave=false) — the same call `publishVideo` makes before
      // serialising. It keeps the publish action stamped above; otherwise it
      // records a plain save, as the canonical render writes its own action.
      // `saveStoryAndVideo` internally awaits `waitForBackgroundSave`, so the
      // record has a persisted id on return.
      await videoCreator.saveStoryAndVideo(
        false,
        true,
        false,
        false,
        !!translatedPublish,
      );
    },

    getVideoForAspectRatio: aspectRatio => {
      if (mainClip.aspectRatio === aspectRatio) return mainClip;
      return mainClip.associatedVideos?.find(
        v => v.aspectRatio === aspectRatio,
      ) as Video | undefined;
    },

    populateResources: video => {
      const storyId = videoCreator.story?.id;
      if (!storyId) {
        throw new Error('Cannot populate resources: story not set');
      }
      return populateVideoVersionWithResources(
        storyId,
        video,
        [
          'videoSourceSnapshot',
          'transcriptionSnapshot',
          'karaokeData',
          'scenes',
        ],
        true, // skipIfAlreadyPopulated
        'latest',
        false, // throwIfMissing — karaokeData may not exist yet
      );
    },

    // The story's dub in one language, through the same repository the Voice
    // control uses, so the publish path and the preview path cannot drift apart.
    fetchDub: language => {
      const storyId = videoCreator.story?.id;
      if (!storyId) {
        throw new Error('Cannot fetch dub: story not set');
      }
      return videoCreator.datoClientStore.dubbingRepository.getDubManifest({
        storyId,
        language,
      });
    },

    // The kept music bed, through the same repository the separation control
    // reads, so the publish path and the screen cannot disagree about which
    // bed a story has.
    fetchStemUrls: async () => {
      const none = { bedUrl: null, voiceUrl: null };
      const storyId = videoCreator.story?.id;
      if (!storyId) return none;
      try {
        const summary =
          await videoCreator.datoClientStore.dubbingRepository.getStemsSummary({
            storyId,
          });
        if (!summary.hasStems) return none;
        return {
          bedUrl: summary.noMusic ? null : (summary.bedUrl ?? null),
          voiceUrl: summary.voiceUrlMp3 ?? summary.voiceUrlWav ?? null,
        };
      } catch {
        // Advisory: a render without music under the dub beats a failed
        // publish.
        return none;
      }
    },

    getOriginalTranscriptElements: () =>
      rootStore.transcriptionStore.getOriginalTranscription()?.elements ?? [],

    getTranslationPhrases: language =>
      rootStore.translationStore.getTranslation(language)?.phrases ?? null,

    getCompositionPhrases: language =>
      rootStore.translationStore.getCompositionPhrasesByNormalizedSource(
        language,
      ),

    buildSource: (base, language, resLevel) =>
      sourceBuilder.buildSource(base, language, resLevel, (source, res) =>
        videoCreator.replaceWithValidVideoSourceUrl(source, res),
      ),

    getSourceVideoUrl: resLevel =>
      videoCreator.getValidVideoSourceUrlForResLevel(resLevel),

    getSongsData: () => {
      const stockSongs = videoCreator.stockMusic.flatMap(m => m.collection);
      const ownSongs = videoCreator.story?.myAudios?.map(o => o.song) ?? [];
      return [...stockSongs, ...ownSongs];
    },

    createPublisher: video =>
      new VideoPublisher(
        {
          story: videoCreator.story!,
          organization: videoCreator.organization,
          video,
        },
        rootStore.analyticsStore,
        rootStore.userIdentityStore,
      ),

    createRecordsForCommand: (cells, groupId) =>
      publishRecordService.createRecordsForCommand({
        storyId: videoCreator.story?.id ?? '',
        cells,
        groupId,
        publishedBy: rootStore.userIdentityStore.currentIdentity?.name ?? null,
      }),

    markRecordFailed: (recordId, errorMessage) =>
      publishRecordService.markFailed(recordId, errorMessage),

    trackRecords: records =>
      rootStore.publishTrackingStore.trackRecords(records),

    markDispatchFailed: (recordId, errorMessage) =>
      rootStore.publishTrackingStore.markFailed(recordId, errorMessage),
  };
}
